import { z } from 'zod';
import dayjs from 'dayjs';
import { zodResolver } from '@hookform/resolvers/zod';
import { roundNumber } from '~/modules/@shared/helpers/roundNumber';

const baseSchema = z.object({
  member_id: z.coerce.number({ invalid_type_error: 'Required' }).min(1, { message: 'Required' }).optional(),
  payment_id: z.number().optional(),
  payment_method_id: z.coerce.number({ invalid_type_error: 'Required' }).min(0, { message: 'Required' }),
  amount: z.coerce
    .number({ invalid_type_error: 'Required' })
    .min(0.01, { message: 'Enter an amount greater than 0' })
    .transform((x) => roundNumber({ value: x, decimals: 2 })),
  scheduled_date: z
    .string()
    .min(1, { message: 'Required' })
    .transform((v) => dayjs(v).format('YYYY-MM-DD')),
  description: z.string().trim().optional(),
  taxable: z.boolean(),
});

const isDeaSchema = z
  .object({
    isDea: z.literal(true),
  })
  .merge(baseSchema);

const regularSchema = z
  .object({
    isDea: z.literal(false),
    payment_category_type_id: z.coerce.number({ invalid_type_error: 'Required' }).optional(),
    //.refine((x) => x !== undefined, { message: 'Required' }),
  })
  .merge(baseSchema);

const s1 = baseSchema.merge(isDeaSchema);
const s2 = baseSchema.merge(regularSchema);

const takePaymentSchema = z.discriminatedUnion('isDea', [s1, s2]);

const schema = takePaymentSchema.refine(
  (data) => {
    return data.member_id !== undefined && data.member_id > 0;
  },
  {
    message: 'Required',
    path: ['member_id'],
  },
);

type TakePaymentFormDataVariantMap = {
  isDea: z.infer<typeof isDeaSchema>;
  regular: z.infer<typeof regularSchema>;
};

export type GetVariant<T extends keyof TakePaymentFormDataVariantMap> = TakePaymentFormDataVariantMap[T];

export type TakePaymentFormData = z.infer<typeof schema>;

export const takePaymentFormResolver = zodResolver(schema);

export const takePaymentFormDefault: TakePaymentFormData = {
  isDea: false,
  member_id: undefined,
  payment_method_id: 0,
  payment_category_type_id: undefined,
  payment_id: undefined,
  amount: 0,
  scheduled_date: dayjs().format('YYYY-MM-DD'),
  description: '',
  taxable: false,
};
